import React, { useEffect, useRef, useState } from 'react';
import { createPeerConnection, createWebSocketConnection } from '../utils/webrtcConfig';

export default function OutpatientJoin() {
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const [status, setStatus] = useState('Connecting...');

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const roomId = params.get('room');
        if (!roomId) { setStatus('Missing room id'); return; }

        // Setup peer connection with comprehensive STUN/TURN servers for global connectivity
        const pc = createPeerConnection();
        pcRef.current = pc;

        // Local media (patient)
        const local = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        local.getTracks().forEach(t => pc.addTrack(t, local));
        if (localRef.current) localRef.current.srcObject = local;

        const remote = new MediaStream();
        pc.ontrack = (ev) => {
          ev.streams[0].getTracks().forEach(tr => remote.addTrack(tr));
          if (remoteRef.current) remoteRef.current.srcObject = remote;
        };

        // WebSocket signaling (supports both dev and live deployment)
        let ws;
        try {
          ws = await createWebSocketConnection(`room/${roomId}`);
        } catch (e) {
          // Local fallback: BroadcastChannel signaling when both tabs are on same origin
          try {
            const bc = new BroadcastChannel(`rtc_${roomId}`);
            wsRef.current = {
              readyState: 1,
              send: (payload) => bc.postMessage(payload),
              close: () => { try { bc.close(); } catch {} },
            };
            bc.onmessage = async (ev) => {
              try {
                const msg = JSON.parse(ev.data || ev);
                if (msg.sdp) {
                  await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
                  if (msg.sdp.type === 'offer') {
                    const answer = await pc.createAnswer();
                    await pc.setLocalDescription(answer);
                    bc.postMessage(JSON.stringify({ sdp: pc.localDescription }));
                    setStatus('Connected');
                  }
                } else if (msg.candidate) {
                  try { await pc.addIceCandidate(msg.candidate); } catch {}
                }
              } catch {}
            };
            setStatus('Using local signaling');
          } catch {
            setStatus('Unable to connect signaling.');
            return;
          }
        }
        
        if (ws) {
          wsRef.current = ws;
        }
        const pendingCandidates = [];

        ws.onmessage = async (ev) => {
          const msg = JSON.parse(ev.data || '{}');
          if (msg.type === 'hello') {
            // Caller announced presence; if we already have a localDescription, no action; else wait for offer
            return;
          }
          if (msg.type === 'hangup') {
            try { ws.close(); } catch {}
            try { pc.close(); } catch {}
            window.close && window.close();
            return;
          }
          if (msg.sdp) {
            await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
            if (msg.sdp.type === 'offer') {
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              ws.send(JSON.stringify({ sdp: pc.localDescription }));
              setStatus('Connected');
            }
          } else if (msg.candidate) {
            try { await pc.addIceCandidate(msg.candidate); } catch {}
          }
        };

        pc.onicecandidate = (ev) => {
          if (ev.candidate) {
            const payload = JSON.stringify({ candidate: ev.candidate });
            if (ws.readyState === 1) {
              try { ws.send(payload); } catch {}
            } else {
              pendingCandidates.push(payload);
            }
          }
        };

        if (ws && ws.readyState === 1) {
          // If socket is already open, flush any queued ICE immediately
          while (pendingCandidates.length && ws.readyState === 1) {
            try { ws.send(pendingCandidates.shift()); } catch { break; }
          }
          try { ws.send(JSON.stringify({ type: 'hello' })); } catch {}
        } else if (ws && ws.addEventListener) {
          ws.addEventListener('open', async () => {
            while (pendingCandidates.length && ws.readyState === 1) {
              try { ws.send(pendingCandidates.shift()); } catch { break; }
            }
            try { ws.send(JSON.stringify({ type: 'hello' })); } catch {}
          });
        }
      } catch (e) {
        setStatus('Failed to initialize.');
      }
    })();

    return () => {
      try { wsRef.current && wsRef.current.close(); } catch {}
      try { pcRef.current && pcRef.current.close(); } catch {}
    };
  }, []);

  return (
    <div style={{ padding: 16 }}>
      <h2>Join Consultation</h2>
      <div style={{ marginBottom: 8, opacity: 0.7 }}>{status}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <video ref={localRef} autoPlay muted playsInline style={{ width: '100%', background:'#000', borderRadius: 8 }} />
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>You</div>
        </div>
        <div>
          <video ref={remoteRef} autoPlay playsInline style={{ width: '100%', background:'#000', borderRadius: 8 }} />
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>Doctor</div>
        </div>
      </div>
    </div>
  );
}